import React, { useState, useEffect, useRef } from 'react';
import HeartParticles from './components/HeartParticles';
import PhotoboothPage from './components/PhotoboothPage';
import RecapGallery from './components/RecapGallery';
import WaSendModal from './components/WaSendModal';

// ── Marriage counter ──
const WEDDING = new Date('2023-11-11');
function getDays() { return Math.floor((Date.now() - WEDDING) / 86400000); }

// ── Terminal boot lines ──
const BOOT = [
  { t: '[boot]  NofOS v6.0 — Nof Birthday Edition', c: 'var(--c-pink)' },
  { t: '[init]  love.service: starting...', c: 'var(--c-purple)' },
  { t: '[  ok ] heart.ko: loaded', c: 'var(--c-green)' },
  { t: `[  ok ] memory.ko: ${getDays()} hari kebersamaan dengan Nof`, c: 'var(--c-green)' },
  { t: '[  ok ] camera.service: active', c: 'var(--c-green)' },
  { t: '[warn]  happiness_level: MAXIMUM — Nof detected', c: 'var(--c-amber)' },
  { t: '', c: '' },
  { t: 'Selamat datang, Nof. Hari ini milikmu. 💖', c: 'var(--c-pink)' },
  { t: 'Ketik  help  untuk melihat perintah.', c: 'var(--c-cyan)' },
];

const CMDS = {
  help: { lines: [
    { t: '╔══════════════════════════════════╗', c: 'var(--c-cyan)' },
    { t: "║    HAPPY BIRTHDAY NOF — v6.0     ║", c: 'var(--c-pink)' },
    { t: '╠══════════════════════════════════╣', c: 'var(--c-cyan)' },
    { t: '║  photobooth  →  Buka kamera Nof  ║', c: 'var(--text)' },
    { t: '║  recap       →  Galeri kenangan  ║', c: 'var(--text)' },
    { t: '║  whoami      →  Kamu itu siapa   ║', c: 'var(--text)' },
    { t: '║  love        →  Surat untuk Nof  ║', c: 'var(--text)' },
    { t: '║  age         →  Usia pernikahan  ║', c: 'var(--text)' },
    { t: '║  clear       →  Bersihkan layar  ║', c: 'var(--text)' },
    { t: '╚══════════════════════════════════╝', c: 'var(--c-cyan)' },
  ]},
  whoami: { lines: [
    { t: '┌─ Identity: Nof ─────────────────────┐', c: 'var(--c-pink)' },
    { t: '│  Name   : Nof                       │', c: 'var(--text-dim)' },
    { t: '│  Role   : Istri terbaik di dunia    │', c: 'var(--text-dim)' },
    { t: '│  Access : ROOT — full heart access  │', c: 'var(--text-dim)' },
    { t: '│  Since  : 11 November 2023 💍       │', c: 'var(--text-dim)' },
    { t: '└─────────────────────────────────────┘', c: 'var(--c-pink)' },
  ]},
  age: { lines: [
    { t: '$ uptime --love --for=Nof', c: 'var(--c-purple)' },
    { t: '', c: '' },
    { t: `  Usia pernikahan  : ${getDays()} hari`, c: 'var(--text-dim)' },
    { t: `  Mulai sejak      : 11 November 2023`, c: 'var(--text-dim)' },
    { t: `  Uptime           : 100.000%`, c: 'var(--c-green)' },
    { t: `  Downtime         : 0 detik`, c: 'var(--c-green)' },
  ]},
  love: { lines: [
    { t: '$ cat /dev/heart/untuk-nof.txt', c: 'var(--c-purple)' },
    { t: '', c: '' },
    { t: '  Nof,', c: 'var(--text-dim)' },
    { t: '  Setiap pagi aku bangun dan bersyukur', c: 'var(--text-dim)' },
    { t: '  kamu ada di sisiku. Kamu bukan hanya', c: 'var(--text-dim)' },
    { t: '  istriku — kamu adalah rumahku, Nof.', c: 'var(--text-dim)' },
    { t: '', c: '' },
    { t: '  Selamat ulang tahun, Nof. 🎂', c: 'var(--c-pink)' },
    { t: '', c: '' },
    { t: '               — Suamimu ❤️', c: 'var(--c-amber)' },
  ]},
  photobooth: { nav: 'photobooth' },
  recap: { nav: 'recap' },
};

// ── Terminal ──
function Terminal({ onNav }) {
  const [hist, setHist] = useState([]);
  const [inp, setInp] = useState('');
  const [bootIdx, setBootIdx] = useState(0);
  const [booting, setBooting] = useState(true);
  const inpRef = useRef(null);
  const btmRef = useRef(null);

  useEffect(() => { btmRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [hist, bootIdx]);
  useEffect(() => {
    if (!booting) return;
    if (bootIdx < BOOT.length) { const t = setTimeout(() => setBootIdx(p => p + 1), 200); return () => clearTimeout(t); }
    else setBooting(false);
  }, [booting, bootIdx]);
  useEffect(() => { if (!booting) inpRef.current?.focus(); }, [booting]);

  const run = (cmd) => {
    const k = cmd.trim().toLowerCase();
    const h = [...hist, { t: `nof@heart:~$ ${cmd}`, c: 'var(--text-faint)' }];
    if (k === 'clear') { setHist([]); setInp(''); return; }
    const d = CMDS[k];
    if (d) {
      if (d.nav) { h.push({ t: `→ membuka ${d.nav} untuk Nof...`, c: 'var(--c-green)' }); setHist(h); setInp(''); setTimeout(() => onNav(d.nav), 400); return; }
      d.lines.forEach(l => h.push(l));
    } else if (k) {
      h.push({ t: `bash: ${cmd}: command not found`, c: '#f87171' });
    }
    setHist(h); setInp('');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="terminal-scroll">
        {BOOT.slice(0, bootIdx).map((l, i) => (
          <div key={i} className="terminal-line" style={{ color: l.c || 'var(--text)' }}>{l.t}</div>
        ))}
        {booting && bootIdx < BOOT.length && (
          <span className="blink-cursor" style={{ display: 'inline-block', width: 7, height: 13, background: 'var(--terra)', verticalAlign: 'middle' }} />
        )}
        {!booting && hist.map((l, i) => (
          <div key={i} className="terminal-line" style={{ color: l.c || 'var(--text)' }}>{l.t}</div>
        ))}
        {!booting && (
          <form onSubmit={e => { e.preventDefault(); inp.trim() && run(inp); }} className="terminal-input-row">
            <span style={{ color: 'var(--terra)' }}>❯</span>
            <span style={{ color: 'var(--sage)' }}>nof@heart</span>
            <span style={{ color: 'var(--text-faint)' }}>:~$</span>
            <input ref={inpRef} className="terminal-input" value={inp} onChange={e => setInp(e.target.value)} autoComplete="off" spellCheck={false} />
          </form>
        )}
        <div ref={btmRef} />
      </div>
      {!booting && (
        <div className="terminal-quick-btns">
          {['photobooth', 'recap', 'love', 'age', 'whoami'].map(c => (
            <button key={c} className="quick-btn" onClick={() => run(c)}>{c}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Nav ──
const NAV = [
  { id: 'terminal', label: 'Terminal', icon: '⌨️' },
  { id: 'photobooth', label: "Nof's Booth", icon: '📸' },
  { id: 'recap', label: 'Kenangan Nof', icon: '🖼️' },
];

const WaIco = () => (
  <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787" />
  </svg>
);

// ── Theme Toggle Button ──
function ThemeToggle({ dark, onToggle }) {
  return (
    <button onClick={onToggle} title={dark ? 'Mode Terang' : 'Mode Gelap'}
      style={{
        position: 'fixed', top: 14, right: 14, zIndex: 100,
        width: 40, height: 40, borderRadius: '50%',
        background: 'var(--card)',
        border: '1px solid var(--border-warm)',
        cursor: 'pointer', fontSize: 18, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
        transition: 'all 0.2s',
      }}>
      {dark ? '☀️' : '🌙'}
    </button>
  );
}

// ── Main App ──
export default function App() {
  const [view, setView] = useState('terminal');
  const [photos, setPhotos] = useState([]);
  const [lastPhoto, setLastPhoto] = useState(null);
  const [showWa, setShowWa] = useState(false);
  const [toast, setToast] = useState(false);
  const [days, setDays] = useState(getDays());
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem('nof-theme') !== 'light'; } catch { return true; }
  });

  useEffect(() => {
    const t = setInterval(() => setDays(getDays()), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
    try { localStorage.setItem('nof-theme', dark ? 'dark' : 'light'); } catch {}
  }, [dark]);

  const handleCapture = (url) => {
    setPhotos(p => [...p, url]);
    setLastPhoto(url);
    setToast(true);
    setTimeout(() => setToast(false), 4000);
  };

  // Photobooth = dedicated full-screen page
  if (view === 'photobooth') {
    return (
      <>
        <ThemeToggle dark={dark} onToggle={() => setDark(d => !d)} />
        <PhotoboothPage onBack={() => setView('terminal')} onCapture={handleCapture} />
      </>
    );
  }

  const cur = NAV.find(n => n.id === view);

  return (
    <div className="app-shell">
      <ThemeToggle dark={dark} onToggle={() => setDark(d => !d)} />
      <div className="bg-glow-1" />
      <div className="bg-glow-2" />
      <div className="bg-grid" />
      <HeartParticles />

      <div className="app-container">
        {/* Header */}
        <header className="app-header">
          <div className="app-badge">
            <span className="badge-dot" />
            NofOS v6.0 — Happy Birthday, Nof!
          </div>
          <h1 className="app-title">Happy Birthday, <span>Nof!</span></h1>
          <p className="app-subtitle">
            5 Mei 2026 &nbsp;·&nbsp;
            <span className="highlight">{days} hari bersama Nof</span>
            &nbsp;·&nbsp; Sejak 11-11-2023 💍
          </p>
        </header>

        {/* Nav */}
        <nav className="app-nav">
          {NAV.map(n => (
            <button key={n.id} className={`nav-btn${view === n.id ? ' active' : ''}`} onClick={() => setView(n.id)}>
              {n.icon} {n.label}
              {n.id === 'photobooth' && photos.length > 0 && (
                <span className="nav-badge">{photos.length}</span>
              )}
            </button>
          ))}
        </nav>

        {/* Card */}
        <main className="app-card">
          <div className="card-bar">
            <div className="card-dots">
              <div className="dot dot-r" /><div className="dot dot-y" /><div className="dot dot-g" />
            </div>
            <div className="card-title-bar">{cur?.icon} {cur?.label} — nof@birthday:~</div>
            {lastPhoto && (
              <button className="card-wa-btn" onClick={() => setShowWa(true)}>
                <WaIco /> WA Nof
              </button>
            )}
          </div>
          <div className="card-body">
            {view === 'terminal' && <Terminal onNav={setView} />}
            {view === 'recap' && (
              <div className="card-padding">
                <RecapGallery capturedPhotos={photos} />
              </div>
            )}
          </div>
        </main>

        <footer className="app-footer">
          Dibuat dengan ❤️ untuk Nof &nbsp;·&nbsp; HBD 05.05.2026 &nbsp;·&nbsp; {days} hari bersama
        </footer>
      </div>

      {showWa && lastPhoto && (
        <WaSendModal isOpen onClose={() => setShowWa(false)} imageDataUrl={lastPhoto} />
      )}

      {toast && (
        <div className="toast">
          <span style={{ fontSize: 22 }}>📸</span>
          <div>
            <p style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>Foto Nof berhasil!</p>
            <p style={{ color: 'var(--text-faint)', fontSize: 12, margin: '2px 0 0' }}>Klik untuk kirim ke WA</p>
          </div>
          <button className="toast-send-btn" onClick={() => { setToast(false); setShowWa(true); }}>Kirim WA</button>
        </div>
      )}
    </div>
  );
}